import { useCallback, useMemo, useSyncExternalStore, type PropsWithChildren } from "react";

import { useJsonLocalStorage } from "@/shared/hooks/useJsonLocalStorage";

import {
  isThemePreference,
  ThemeContext,
  themeContextDefaultValue,
  type ThemeContextThemeOption,
  type ThemePreference,
} from "./theme-context";

const THEME_STORAGE_KEY = "admin-theme-preference";

const DARK_SCHEME_QUERY = "(prefers-color-scheme: dark)";

const THEME_OPTIONS: ThemeContextThemeOption[] = [
  { id: "light", label: "Light", description: "Always use the light theme" },
  { id: "dark", label: "Dark", description: "Always use the dark theme" },
  { id: "system", label: "System", description: "Follow your device setting" },
];

const getMediaQuery = (): MediaQueryList | null => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return null;
  }

  try {
    return window.matchMedia(DARK_SCHEME_QUERY);
  } catch {
    return null;
  }
};

const subscribeToSystemScheme = (callback: () => void) => {
  const mediaQuery = getMediaQuery();

  if (!mediaQuery) {
    return () => {};
  }

  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", callback);
    return () => mediaQuery.removeEventListener("change", callback);
  }

  mediaQuery.addListener(callback);
  return () => mediaQuery.removeListener(callback);
};

const getSystemSnapshot = () => getMediaQuery()?.matches ?? false;

// Rendered during SSG prerendering, where the preference is unknowable —
// prerendered (marketing) markup is always light.
const getServerSnapshot = () => false;

const sanitizeThemePreference = (value: unknown): ThemePreference =>
  isThemePreference(value) ? value : themeContextDefaultValue.themePreference;

export const ThemeProvider = ({ children }: PropsWithChildren) => {
  const [themePreference, setThemePreference] = useJsonLocalStorage(THEME_STORAGE_KEY, sanitizeThemePreference);
  const systemPrefersDark = useSyncExternalStore(subscribeToSystemScheme, getSystemSnapshot, getServerSnapshot);

  const selectTheme = useCallback(
    (preference: ThemePreference) => {
      setThemePreference((current) => (isThemePreference(preference) ? preference : current));
    },
    [setThemePreference],
  );

  const resolvedTheme = themePreference === "system" ? (systemPrefersDark ? "dark" : "light") : themePreference;

  const value = useMemo(
    () => ({
      themePreference,
      resolvedTheme,
      themeOptions: THEME_OPTIONS,
      selectTheme,
    }),
    [themePreference, resolvedTheme, selectTheme],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
};
